import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import type { IMultiSelectProps } from './MultiSelect.types';
import { Icon } from '@ui-library/Icon/Icon';
import { IconName } from '@ui-library/Icon/Icon.type';

export const MultiSelect: React.FC<IMultiSelectProps> = ({
    label,
    helperText,
    errorText,
    size = 'md',
    variant = 'default',
    startIcon,
    endIcon,
    className = '',
    containerClassName = '',
    labelClassName = '',
    required = false,
    disabled = false,
    options,
    value,
    onChange,
    placeholder = 'Select...',
    allSelectedLabel = 'All',
    noneSelectedLabel,
    showSelectAll = true,
    selectAllLabel = 'All platforms',
    id,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const selectedSet = useMemo(() => new Set(value), [value]);
    const allSelected = options.length > 0 && value.length === options.length;
    const noneSelected = value.length === 0;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen) {
            setHighlightedIndex(showSelectAll ? 0 : 0);
        }
    }, [isOpen, showSelectAll]);

    const totalRows = (showSelectAll ? 1 : 0) + options.length;

    const toggleValue = (optionValue: string) => {
        if (selectedSet.has(optionValue)) {
            onChange(value.filter(v => v !== optionValue));
        } else {
            onChange([...value, optionValue]);
        }
    };

    const toggleAll = () => {
        if (allSelected) {
            onChange([]);
        } else {
            onChange(options.map(opt => opt.value));
        }
    };

    const handleRowActivate = (rowIndex: number) => {
        if (showSelectAll && rowIndex === 0) {
            toggleAll();
            return;
        }
        const optIndex = showSelectAll ? rowIndex - 1 : rowIndex;
        if (optIndex >= 0 && optIndex < options.length) {
            toggleValue(options[optIndex].value);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;
        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (isOpen) {
                    if (highlightedIndex >= 0 && highlightedIndex < totalRows) {
                        handleRowActivate(highlightedIndex);
                    }
                } else {
                    setIsOpen(true);
                }
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                } else {
                    setHighlightedIndex(prev => (prev < totalRows - 1 ? prev + 1 : prev));
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                } else {
                    setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
                }
                break;
            case 'Escape':
                if (isOpen) {
                    e.preventDefault();
                    setIsOpen(false);
                    triggerRef.current?.focus();
                }
                break;
            case 'Tab':
                if (isOpen) setIsOpen(false);
                break;
        }
    };

    const sizeMapper = {
        'sm': {
            input: 'px-bg-v2-sm py-bg-v2-xs text-bg-v2-sm',
            withStartIcon: 'pl-bg-v2-4xl',
            withEndIcon: 'pr-bg-v2-4xl',
            icon: 'w-4 h-4',
            iconPosition: 'left-bg-v2-sm right-bg-v2-sm',
            option: 'px-bg-v2-sm py-bg-v2-xs text-bg-v2-sm',
            checkbox: 'w-4 h-4',
            checkmark: 'w-2.5 h-2.5',
        },
        'md': {
            input: 'px-bg-v2-lg py-bg-v2-md text-bg-v2-md',
            withStartIcon: 'pl-bg-v2-5xl',
            withEndIcon: 'pr-bg-v2-5xl',
            icon: 'w-5 h-5',
            iconPosition: 'left-bg-v2-lg right-bg-v2-lg',
            option: 'px-bg-v2-lg py-bg-v2-md text-bg-v2-md',
            checkbox: 'w-[18px] h-[18px]',
            checkmark: 'w-3 h-3',
        },
        'lg': {
            input: 'px-bg-v2-lg py-bg-v2-md text-bg-v2-lg',
            withStartIcon: 'pl-bg-v2-6xl',
            withEndIcon: 'pr-bg-v2-6xl',
            icon: 'w-6 h-6',
            iconPosition: 'left-bg-v2-lg right-bg-v2-lg',
            option: 'px-bg-v2-lg py-bg-v2-md text-bg-v2-lg',
            checkbox: 'w-5 h-5',
            checkmark: 'w-4 h-4',
        },
    };

    const variantMapper = {
        'default': 'border-border-primary text-text-primary bg-bg-primary focus:border-border-brand placeholder:text-text-placeholder focus:border-2',
        'error': 'border-border-error text-text-primary bg-bg-primary focus:border-border-error placeholder:text-text-placeholder focus:border-2',
        'success': 'border-border-brand text-text-primary bg-bg-primary focus:border-border-brand placeholder:text-text-placeholder focus:border-2',
        'regular': 'border-border-primary text-text-regular bg-bg-primary focus:border-border-brand placeholder:text-text-placeholder focus:border-2',
    };

    const actualVariant = errorText ? 'error' : variant;
    const selectId = id || `multi-select-${Math.random().toString(36).substring(2, 11)}`;

    const triggerText = (() => {
        if (allSelected) return allSelectedLabel;
        if (noneSelected) return noneSelectedLabel ?? placeholder;
        return options
            .filter(opt => selectedSet.has(opt.value))
            .map(opt => opt.initial ?? opt.label)
            .join(', ');
    })();

    const triggerClasses = `
    w-full
    flex items-center justify-between
    ${sizeMapper[size].input}
    ${startIcon ? sizeMapper[size].withStartIcon : ''}
    ${endIcon ? sizeMapper[size].withEndIcon : ''}
    ${variantMapper[actualVariant]}
    border
    rounded-bg-v2-md
    transition-all
    transition-colors
    duration-200
    focus:outline-none
    cursor-pointer
    ${disabled ? 'bg-bg-disabled text-text-disabled border-border-disabled cursor-not-allowed' : 'hover:bg-bg-primary'}
    ${className}
  `.trim().replace(/\s+/g, ' ');

    const renderCheckbox = (checked: boolean, indeterminate = false) => (
        <span
            className={`
                relative inline-flex items-center justify-center shrink-0
                ${sizeMapper[size].checkbox}
                border rounded-bg-v2-sm transition-colors duration-200
                ${checked || indeterminate
                    ? 'bg-bg-brand-solid border-border-brand'
                    : 'bg-bg-primary border-border-primary'}
            `.trim().replace(/\s+/g, ' ')}
            aria-hidden="true"
        >
            {checked && !indeterminate && (
                <Icon name={IconName.Check} size="sm" className="text-text-white" inheritColor />
            )}
            {indeterminate && (
                <span className="block w-2 h-0.5 bg-text-white rounded-sm" />
            )}
        </span>
    );

    return (
        <div className={`flex flex-col gap-bg-v2-sm w-full relative ${containerClassName}`} ref={containerRef}>
            {label && (
                <label
                    htmlFor={selectId}
                    className={`font-bg-v2-sans font-bg-v2-medium text-bg-v2-sm text-text-secondary ${labelClassName}`}
                >
                    {label}
                </label>
            )}

            <div className="relative w-full">
                {startIcon && (
                    <div className={`absolute ${size === 'sm' ? 'left-bg-v2-sm' : 'left-bg-v2-lg'} top-1/2 -translate-y-1/2 ${sizeMapper[size].icon} text-text-quaternary pointer-events-none z-10`}>
                        {startIcon}
                    </div>
                )}

                <button
                    ref={triggerRef}
                    id={selectId}
                    type="button"
                    className={triggerClasses}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-labelledby={label ? undefined : selectId}
                    {...props}
                >
                    <span className={`font-bg-v2-sans font-bg-v2-regular truncate ${noneSelected ? 'text-text-placeholder' : ''}`}>
                        {triggerText}
                    </span>
                    <div className={`flex items-center ${sizeMapper[size].icon} text-text-tertiary transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}>
                        {endIcon || <ChevronDown className="w-full h-full" />}
                    </div>
                </button>

                {errorText && (
                    <div className={`absolute ${size === 'sm' ? 'right-bg-v2-sm' : 'right-bg-v2-lg'} top-1/2 -translate-y-1/2 ${sizeMapper[size].icon} text-text-error-primary pointer-events-none mr-6 z-10`}>
                        <Icon name={IconName.Alert} size="md" />
                    </div>
                )}
            </div>

            {isOpen && !disabled && (
                <div
                    ref={listRef}
                    className="absolute top-full left-0 mt-1 w-full bg-bg-primary rounded-bg-v2-lg shadow-bg-v2-lg border border-border-primary py-1 z-20 animate-fade-in-down max-h-[420px] overflow-auto scrollbar-hide"
                    role="listbox"
                    aria-multiselectable="true"
                >
                    {showSelectAll && (
                        <div
                            role="option"
                            aria-selected={allSelected}
                            className={`
                                flex items-center gap-bg-v2-md w-full cursor-pointer
                                ${sizeMapper[size].option} font-bg-v2-sans transition-colors
                                ${highlightedIndex === 0 ? 'bg-bg-secondary' : ''}
                                border-b border-border-secondary
                            `.trim().replace(/\s+/g, ' ')}
                            onClick={() => handleRowActivate(0)}
                            onMouseEnter={() => setHighlightedIndex(0)}
                        >
                            {renderCheckbox(allSelected, !allSelected && !noneSelected)}
                            <span className="font-bg-v2-medium text-text-primary truncate">
                                {selectAllLabel}
                            </span>
                        </div>
                    )}

                    {options.map((option, index) => {
                        const rowIndex = showSelectAll ? index + 1 : index;
                        const isChecked = selectedSet.has(option.value);
                        return (
                            <div
                                key={option.value}
                                role="option"
                                aria-selected={isChecked}
                                className={`
                                    flex items-center gap-bg-v2-md w-full cursor-pointer
                                    ${sizeMapper[size].option} font-bg-v2-sans font-bg-v2-regular transition-colors
                                    ${highlightedIndex === rowIndex ? 'bg-bg-secondary' : ''}
                                    ${isChecked ? 'text-text-primary' : 'text-text-primary'}
                                `.trim().replace(/\s+/g, ' ')}
                                onClick={() => handleRowActivate(rowIndex)}
                                onMouseEnter={() => setHighlightedIndex(rowIndex)}
                            >
                                {renderCheckbox(isChecked)}
                                <span className="truncate flex-1">
                                    {option.label}
                                </span>
                                {option.initial && (
                                    <span className="shrink-0 text-text-tertiary font-bg-v2-medium text-bg-v2-xs px-bg-v2-sm py-bg-v2-xxs rounded-bg-v2-sm bg-bg-secondary">
                                        {option.initial}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {errorText && (
                <span className="font-bg-v2-sans font-bg-v2-regular text-bg-v2-sm text-text-error-primary">
                    {errorText}
                </span>
            )}

            {helperText && !errorText && (
                <span className="font-bg-v2-sans font-bg-v2-regular text-bg-v2-sm text-text-tertiary">
                    {helperText}
                </span>
            )}
        </div>
    );
};
